import React, { useState, useEffect, useRef } from 'react';
import { BrowserRouter as Router, Routes, Route, useParams } from 'react-router-dom';
import { FormProvider, useFormContext } from './context/FormContext';
import ProgressBar from './components/ProgressBar';
import Navigation from './components/Navigation';
import Section1SellerInfo from './components/sections/Section1SellerInfo';
import Section2Communication from './components/sections/Section2Communication';
import Section3SellingPlans from './components/sections/Section3SellingPlans';
import Section4Showing from './components/sections/Section4Showing';
import Section5PropertyBasics from './components/sections/Section5PropertyBasics';
import Section6InteriorUtilities from './components/sections/Section6InteriorUtilities';
import Section7HomeFeatures from './components/sections/Section7HomeFeatures';
import Section8HOA from './components/sections/Section8HOA';
import Section9Renovations from './components/sections/Section9Renovations';
import AdminDashboard from './components/AdminDashboard';
import AddressAutocomplete, { AddressAutocompleteHandle } from './components/AddressAutocomplete';
import './App.css';

// Welcome Component for new intakes
function WelcomeScreen({ onStart }: { onStart: (address: string, ownerCount: number) => void }) {
  const [propertyAddress, setPropertyAddress] = useState('');
  const [ownerCount, setOwnerCount] = useState(1);
  const addressRef = useRef<AddressAutocompleteHandle>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const address = (addressRef.current?.getValue() || propertyAddress).trim();
    if (address) {
      onStart(address, ownerCount);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4">
      <div className="max-w-md w-full bg-white rounded-lg shadow-md p-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Seller Homework</h1>
        <p className="text-gray-600 mb-6">
          Welcome! This form helps us gather all the information needed to successfully sell your property. 
          Your progress is automatically saved, so you can return anytime.
        </p>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Property Address *
            </label>
            <AddressAutocomplete
              ref={addressRef}
              name="street-address"
              value={propertyAddress}
              onChange={setPropertyAddress}
              placeholder="123 Main Street, City, State 12345"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Owners *
            </label>
            <select
              value={ownerCount}
              onChange={(e) => setOwnerCount(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-brand-primary"
            >
              <option value={1}>1 Owner</option>
              <option value={2}>2 Owners</option>
            </select>
          </div>

          <button
            type="submit"
            className="w-full bg-brand-primary text-white py-2 px-4 rounded-lg hover:bg-brand-primary-dark transition-colors"
          >
            Start Form
          </button>
        </form>
      </div>
    </div>
  );
}

// Main Form Component
function MainForm() {
  const { state, goToSection, calculateCompletion, submitForm } = useFormContext();

  const renderSection = () => {
    switch (state.currentSection) {
      case 1:
        return <Section1SellerInfo />;
      case 2:
        return <Section2Communication />;
      case 3:
        return <Section3SellingPlans />;
      case 4:
        return <Section4Showing />;
      case 5:
        return <Section5PropertyBasics />;
      case 6:
        return <Section6InteriorUtilities />;
      case 7:
        return <Section7HomeFeatures />;
      case 8:
        return <Section8HOA />;
      case 9:
        return <Section9Renovations />;
      default:
        return <Section1SellerInfo />;
    }
  };

  const handleSubmit = async () => {
    const completion = calculateCompletion();
    if (completion < 80) {
      const confirmed = window.confirm(`You're only ${completion}% complete. Are you sure you want to submit? You can always return to complete more details later.`);
      if (!confirmed) {
        return;
      }
    }
    await submitForm();
    window.alert('Form submitted successfully! Thank you for completing the Seller Homework.');
  };

  if (state.isLoading && !state.intake) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800">{state.error}</p>
          </div>
          <button
            onClick={() => window.location.reload()}
            className="bg-brand-primary text-white px-4 py-2 rounded-lg hover:bg-brand-primary-dark"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-brand-primary shadow-sm border-b border-brand-primary-dark">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <img
            src="/metalios-logo.png"
            alt="Metalios"
            className="h-10"
          />
          <p className="text-sm text-brand-gold">Seller Homework</p>
          {state.intake && (
            <p className="text-sm text-white/80">{state.intake.property_address}</p>
          )}
        </div>
      </div>

      {/* Progress Bar */}
      <ProgressBar
        currentSection={state.currentSection}
        totalSections={9}
        completionPercentage={calculateCompletion()}
        onSectionClick={goToSection}
      />

      {/* Form Content */}
      <main className="pb-24">
        {renderSection()}
      </main>

      {/* Navigation */}
      <Navigation onSubmit={handleSubmit} />
    </div>
  );
}

// Intake Loader Component
function IntakeLoader() {
  const { accessToken } = useParams<{ accessToken: string }>();
  const { state, loadIntake, createIntake } = useFormContext();

  useEffect(() => {
    if (accessToken) {
      loadIntake(accessToken);
    }
  // loadIntake is recreated on every render; accessToken alone controls when to reload
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accessToken]);

  const handleStart = async (address: string, ownerCount: number) => {
    await createIntake(address, ownerCount);
  };

  if (state.isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand-primary mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your intake...</p>
        </div>
      </div>
    );
  }

  if (!state.intake && !accessToken) {
    return <WelcomeScreen onStart={handleStart} />;
  }

  if (!state.intake && accessToken) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center max-w-md">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-800">Intake not found or link has expired.</p>
          </div>
          <WelcomeScreen onStart={handleStart} />
        </div>
      </div>
    );
  }

  return <MainForm />;
}

// Main App Component
function App() {
  return (
    <Router>
      <FormProvider>
        <Routes>
          <Route path="/" element={<IntakeLoader />} />
          <Route path="/intake/:accessToken" element={<IntakeLoader />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </FormProvider>
    </Router>
  );
}

export default App;
